import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BankAccount,
  Contract,
  Contractor,
  Department,
  Employee,
  Source,
  Task,
  WorkType,
  createContract,
  createContractor,
  createSource,
  createTask,
  getBankAccounts,
  getContractFormAssignees,
  getContractors,
  getContracts,
  getContractStages,
  getEmployees,
  getExecutorDepartmentEmployees,
  getExecutorDepartments,
  getSources,
  getWorkTypes,
  type ContractStage,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ContractorPicker } from '../components/ContractorPicker';
import { ContractPicker } from '../components/ContractPicker';
import { SearchableSelect } from '../components/SearchableSelect';
import { SourcePicker } from '../components/SourcePicker';
import TaskExecutorsPanel, { ExecutorsDraft } from '../components/TaskExecutorsPanel';
import {
  ContractStagesEditor,
  StageBindPicker,
  contractDatesFromStages,
  datesFromBoundStages,
  emptyStageDraft,
  stagesToWrite,
  validateStageDrafts,
  type StageDraft,
} from '../components/ContractStagesTable';
import MarginCalcTable from '../components/MarginCalcTable';
import {
  BUDGET_EXTRA_EXPENSES_KEY,
  buildMarginCalcHeaderCells,
  buildMarginCalcTableData,
  buildMarginFinanceRows,
  taskMarginFieldValue,
} from '../utils/marginCalc';
import {
  displayContractSumDraft,
  normalizeContractSumDraft,
} from '../utils/table';
import '../styles/DataPage.css';
import '../styles/ContractorDetailPage.css';
import './TaskDetailPage.css';
import './IntoWorkPage.css';

type EntityMode = 'select' | 'create';

type ContractorForm = {
  name: string;
  contractor_type: 'ФЛ' | 'ЮЛ';
  inn: string;
  address: string;
  passport_data: string;
  contact_full_name: string;
  position_relation: string;
  phone: string;
  email: string;
  additional_contacts: string;
};

type ContractForm = {
  is_supplementary: boolean;
  main_contract_id: string;
  contract_number: string;
  contract_subject: string;
  contract_status: string;
  start_date: string;
  end_date: string;
  total_amount: string;
  manager_id: string;
  project_lead_id: string;
  address: string;
  cadastral_number: string;
  source_id: number | null;
  bank_account_id: string;
  has_original: boolean;
};

type TaskFinanceKey =
  | 'amount'
  | 'planned_cost'
  | 'taxes'
  | 'service_fee'
  | 'partner_commission'
  | 'planned_margin'
  | 'adjustment';

type TaskForm = {
  key: string;
  task_id: string;
  short_name: string;
  work_type_id: string;
  task_status: string;
  start_date: string;
  end_date: string;
  stageBindIds: string[];
  amount: string;
  planned_cost: string;
  taxes: string;
  service_fee: string;
  partner_commission: string;
  planned_margin: string;
  adjustment: string;
  manager_id: string;
  project_lead_id: string;
  executors: ExecutorsDraft;
};

function todayDateInputValue(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Разбирает дату из буфера (DD.MM.YYYY, DD.MM.YY, YYYY-MM-DD и т.п.) → YYYY-MM-DD. */
function parsePastedDate(raw: string): string | null {
  const text = raw.trim();
  if (!text) return null;

  const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    const day = Number(iso[3]);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }

  const dmy = text.match(/^(\d{1,2})[./](\d{1,2})[./](\d{2}|\d{4})/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    let year = Number(dmy[3]);
    if (dmy[3].length === 2) {
      year += year >= 70 ? 1900 : 2000;
    }
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }

  return null;
}

function handleDatePaste(
  e: React.ClipboardEvent<HTMLInputElement>,
  onParsed: (value: string) => void,
): void {
  const text = e.clipboardData.getData('text');
  const parsed = parsePastedDate(text);
  if (!parsed) return;
  e.preventDefault();
  onParsed(parsed);
}

function emptyContractorForm(): ContractorForm {
  return {
    name: '',
    contractor_type: 'ЮЛ',
    inn: '',
    address: '',
    passport_data: '',
    contact_full_name: '',
    position_relation: '',
    phone: '',
    email: '',
    additional_contacts: '',
  };
}

function emptyContractForm(): ContractForm {
  return {
    is_supplementary: false,
    main_contract_id: '',
    contract_number: '',
    contract_subject: '',
    contract_status: 'на подписи',
    start_date: todayDateInputValue(),
    end_date: '',
    total_amount: '',
    manager_id: '',
    project_lead_id: '',
    address: '',
    cadastral_number: '',
    source_id: null,
    bank_account_id: '',
    has_original: false,
  };
}

function emptyTaskForm(workTypeId = '', shortName = ''): TaskForm {
  return {
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    task_id: '-',
    short_name: shortName,
    work_type_id: workTypeId,
    task_status: 'черновик',
    start_date: todayDateInputValue(),
    end_date: '',
    stageBindIds: [],
    amount: '',
    planned_cost: '',
    taxes: '',
    service_fee: '',
    partner_commission: '',
    planned_margin: '',
    adjustment: '',
    manager_id: '',
    project_lead_id: '',
    executors: { department_ids: [], assignee_employee_ids: [] },
  };
}

function buildTaskShortName(
  workTypeName: string | undefined,
  contractorName: string,
  contractAddress: string,
): string {
  return `${workTypeName?.trim() || '—'}: ${contractorName.trim() || '—'} / ${contractAddress.trim() || '—'}`;
}

function draftToNumber(value: string): number | undefined {
  const digits = value.trim();
  if (!digits) return undefined;
  const n = Number(digits);
  return Number.isFinite(n) ? n : undefined;
}

function ModeToggle({
  value,
  onChange,
  selectLabel,
  createLabel,
  disabledSelect,
  disabledCreate,
}: {
  value: EntityMode;
  onChange: (mode: EntityMode) => void;
  selectLabel: string;
  createLabel: string;
  disabledSelect?: boolean;
  disabledCreate?: boolean;
}) {
  return (
    <div className="into-work__mode-toggle" role="group">
      <button
        type="button"
        className={`into-work__mode-btn${value === 'select' ? ' into-work__mode-btn--active' : ''}`}
        onClick={() => onChange('select')}
        disabled={disabledSelect}
      >
        {selectLabel}
      </button>
      <button
        type="button"
        className={`into-work__mode-btn${value === 'create' ? ' into-work__mode-btn--active' : ''}`}
        onClick={() => onChange('create')}
        disabled={disabledCreate}
      >
        {createLabel}
      </button>
    </div>
  );
}

const IntoWorkPage: React.FC = () => {
  const navigate = useNavigate();
  const { can, hasDepartment } = useAuth();

  const canOpen =
    hasDepartment('ALL_COMP') &&
    can('CONTRACTORS', 'CREATE') &&
    can('CONTRACTS', 'CREATE') &&
    can('TASKS', 'CREATE');
  const canCreateSum = can('CONTRACT_SUM', 'CREATE') || can('CONTRACT_SUM', 'EDIT');
  const canCreateCost = can('CONTRACT_COST', 'CREATE') || can('CONTRACT_COST', 'EDIT');
  const canCreateMargin = can('CONTRACT_MARGIN', 'CREATE') || can('CONTRACT_MARGIN', 'EDIT');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [managerEmployees, setManagerEmployees] = useState<Employee[]>([]);
  const [projectLeadEmployees, setProjectLeadEmployees] = useState<Employee[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [sourcesLevel2, setSourcesLevel2] = useState<Source[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [executorDepartments, setExecutorDepartments] = useState<Department[]>([]);
  const [deptEmployeeIds, setDeptEmployeeIds] = useState<Record<number, number[]>>({});

  const [contractorMode, setContractorMode] = useState<EntityMode>('select');
  const [selectedContractorId, setSelectedContractorId] = useState<number | null>(null);
  const [contractorForm, setContractorForm] = useState<ContractorForm>(emptyContractorForm);

  const [contractMode, setContractMode] = useState<EntityMode>('select');
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
  const [contractForm, setContractForm] = useState<ContractForm>(emptyContractForm);
  const [stageDrafts, setStageDrafts] = useState<StageDraft[]>(() => [emptyStageDraft()]);
  const [existingStages, setExistingStages] = useState<ContractStage[]>([]);

  const [tasks, setTasks] = useState<TaskForm[]>([]);

  const contractorsById = useMemo(() => {
    const map = new Map<number, Contractor>();
    for (const c of contractors) map.set(c.id, c);
    return map;
  }, [contractors]);

  const defaultWorkTypeId = workTypes[0] ? String(workTypes[0].id) : '';

  const contractorNameForTasks = useMemo(() => {
    if (contractorMode === 'create') return contractorForm.name.trim();
    if (selectedContractorId != null) {
      return contractorsById.get(selectedContractorId)?.name?.trim() ?? '';
    }
    return '';
  }, [contractorMode, contractorForm.name, selectedContractorId, contractorsById]);

  const contractAddressForTasks = useMemo(() => {
    if (contractMode === 'create') return contractForm.address.trim();
    if (selectedContractId != null) {
      return contracts.find((c) => c.id === selectedContractId)?.address?.trim() ?? '';
    }
    return '';
  }, [contractMode, contractForm.address, selectedContractId, contracts]);

  const resolveTaskShortName = useCallback(
    (workTypeId: string): string => {
      const wt = workTypes.find((item) => String(item.id) === workTypeId);
      return buildTaskShortName(wt?.name, contractorNameForTasks, contractAddressForTasks);
    },
    [workTypes, contractorNameForTasks, contractAddressForTasks],
  );

  const marginFinanceRows = useMemo(
    () =>
      buildMarginFinanceRows({
        canSum: canCreateSum,
        canCost: canCreateCost,
        canMargin: canCreateMargin,
      }),
    [canCreateSum, canCreateCost, canCreateMargin],
  );

  useEffect(() => {
    setTasks((prev) => {
      if (prev.length === 0) return prev;
      let changed = false;
      const next = prev.map((task) => {
        const shortName = resolveTaskShortName(task.work_type_id);
        if (task.short_name === shortName) return task;
        changed = true;
        return { ...task, short_name: shortName };
      });
      return changed ? next : prev;
    });
  }, [resolveTaskShortName]);

  const loadData = useCallback(async (): Promise<void> => {
    if (!canOpen) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [
        contractorsData,
        contractsData,
        assigneesData,
        employeesData,
        workTypesData,
        sources2,
        accounts,
        depts,
        deptEmps,
      ] = await Promise.all([
        getContractors(),
        getContracts(),
        getContractFormAssignees(),
        getEmployees(),
        getWorkTypes(),
        getSources(2).catch(() => [] as Source[]),
        getBankAccounts().catch(() => [] as BankAccount[]),
        getExecutorDepartments().catch(() => [] as Department[]),
        getExecutorDepartmentEmployees().catch(() => []),
      ]);
      setContractors(contractorsData);
      setContracts(contractsData);
      setManagerEmployees(assigneesData.managers);
      setProjectLeadEmployees(assigneesData.project_leads);
      setEmployees(employeesData);
      setWorkTypes(workTypesData);
      setSourcesLevel2(sources2);
      setBankAccounts(accounts);
      setExecutorDepartments(depts);
      const idsByDept: Record<number, number[]> = {};
      for (const item of deptEmps) {
        idsByDept[item.department_id] = item.employee_ids;
      }
      setDeptEmployeeIds(idsByDept);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [canOpen]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const employeesByDepartment = useMemo(() => {
    const byId = new Map(employees.map((e) => [e.id, e]));
    const map = new Map<number, Employee[]>();
    for (const dept of executorDepartments) {
      const ids = deptEmployeeIds[dept.id] ?? [];
      map.set(
        dept.id,
        ids.map((id) => byId.get(id)).filter((e): e is Employee => Boolean(e)),
      );
    }
    return map;
  }, [employees, executorDepartments, deptEmployeeIds]);

  const managerOptions = useMemo(
    () =>
      managerEmployees.map((e) => ({
        value: String(e.id),
        label: e.full_name,
      })),
    [managerEmployees],
  );

  const projectLeadOptions = useMemo(
    () =>
      projectLeadEmployees.map((e) => ({
        value: String(e.id),
        label: e.full_name,
      })),
    [projectLeadEmployees],
  );

  const workTypeOptions = useMemo(
    () =>
      workTypes.map((wt) => ({
        value: String(wt.id),
        label: wt.name,
      })),
    [workTypes],
  );

  const bankAccountOptions = useMemo(
    () =>
      bankAccounts.map((a) => ({
        value: String(a.id),
        label: a.currency_type
          ? `${a.planfact_account} (${a.currency_type})`
          : a.planfact_account,
      })),
    [bankAccounts],
  );

  const availableMainContracts = useMemo(
    () =>
      contracts
        .filter(
          (c) =>
            c.contractor_id === selectedContractorId &&
            (c.hierarchy_level ?? 1) === 1,
        )
        .sort((a, b) =>
          a.contract_number.localeCompare(b.contract_number, 'ru', { numeric: true }),
        ),
    [contracts, selectedContractorId],
  );

  const mainContractOptions = useMemo(
    () =>
      availableMainContracts.map((c) => ({
        value: String(c.id),
        label: c.contract_number,
      })),
    [availableMainContracts],
  );

  const hasContractNumberWhitespace = /\s/.test(contractForm.contract_number);

  const hasDuplicateContractNumber = useMemo(() => {
    if (contractMode !== 'create' || selectedContractorId == null) return false;
    const number = contractForm.contract_number.trim();
    if (!number) return false;
    return contracts.some(
      (c) =>
        c.contractor_id === selectedContractorId &&
        c.contract_number.trim().toLowerCase() === number.toLowerCase(),
    );
  }, [contractMode, selectedContractorId, contractForm.contract_number, contracts]);

  const effectiveContractorId = useMemo(() => {
    if (contractorMode === 'select') return selectedContractorId;
    return null;
  }, [contractorMode, selectedContractorId]);

  const contractPickerContracts = useMemo(() => {
    if (contractorMode === 'select' && selectedContractorId != null) {
      return contracts.filter((c) => c.contractor_id === selectedContractorId);
    }
    return contracts;
  }, [contracts, contractorMode, selectedContractorId]);

  const handleContractorMode = (mode: EntityMode): void => {
    setContractorMode(mode);
    setSelectedContractId(null);
    setContractForm(emptyContractForm());
    if (mode === 'create') {
      setSelectedContractorId(null);
      setContractMode('create');
    }
    setError('');
    setSuccess('');
  };

  const handleContractMode = (mode: EntityMode): void => {
    if (mode === 'select' && contractorMode === 'create') return;
    setContractMode(mode);
    setSelectedContractId(null);
    setContractForm(emptyContractForm());
    setStageDrafts([emptyStageDraft()]);
    setExistingStages([]);
    setTasks((prev) => prev.map((task) => ({ ...task, stageBindIds: [] })));
    setError('');
    setSuccess('');
  };

  const handleSelectContractor = (id: number | null): void => {
    setSelectedContractorId(id);
    setSelectedContractId(null);
    setContractForm((prev) => ({ ...prev, main_contract_id: '' }));
    setError('');
    setSuccess('');
  };

  const handleSelectContract = (id: number): void => {
    setSelectedContractId(id);
    const contract = contracts.find((c) => c.id === id);
    if (contract) {
      setContractorMode('select');
      setSelectedContractorId(contract.contractor_id);
    }
    setError('');
    setSuccess('');
  };

  useEffect(() => {
    if (contractMode !== 'select' || selectedContractId == null) {
      setExistingStages([]);
      return;
    }
    let cancelled = false;
    void getContractStages(selectedContractId)
      .then((rows) => {
        if (!cancelled) setExistingStages(rows);
      })
      .catch(() => {
        if (!cancelled) setExistingStages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [contractMode, selectedContractId]);

  useEffect(() => {
    if (contractMode !== 'create') return;
    const dates = contractDatesFromStages(stageDrafts);
    setContractForm((prev) => ({
      ...prev,
      start_date: dates.start_date,
      end_date: dates.end_date,
    }));
  }, [contractMode, stageDrafts]);

  useEffect(() => {
    setTasks((prev) => prev.map((task) => ({ ...task, stageBindIds: [] })));
  }, [selectedContractId]);

  useEffect(() => {
    if (!contractForm.is_supplementary || !contractForm.main_contract_id) return;
    const main = contracts.find((c) => c.id === Number(contractForm.main_contract_id));
    if (!main) return;
    setContractForm((prev) => ({
      ...prev,
      manager_id: main.manager_id != null ? String(main.manager_id) : prev.manager_id,
      project_lead_id:
        main.project_lead_id != null ? String(main.project_lead_id) : prev.project_lead_id,
      address: main.address ?? '',
      cadastral_number: main.cadastral_number ?? '',
      source_id: main.source_id ?? null,
    }));
  }, [contracts, contractForm.is_supplementary, contractForm.main_contract_id]);

  const updateTask = (key: string, patch: Partial<TaskForm>): void => {
    setTasks((prev) =>
      prev.map((t) => {
        if (t.key !== key) return t;
        const next = { ...t, ...patch };
        if (patch.work_type_id != null && patch.short_name == null) {
          next.short_name = resolveTaskShortName(patch.work_type_id);
        }
        return next;
      }),
    );
    setSuccess('');
  };

  const addTask = (): void => {
    const workTypeId = defaultWorkTypeId;
    setTasks((prev) => [
      ...prev,
      emptyTaskForm(workTypeId, resolveTaskShortName(workTypeId)),
    ]);
  };

  const removeTask = (key: string): void => {
    setTasks((prev) => prev.filter((t) => t.key !== key));
  };

  const filledTasks = tasks;

  const stageBindOptions = useMemo(() => {
    if (contractMode === 'create') {
      return stageDrafts.map((row, index) => ({
        id: row.key,
        label: `${index + 1}. ${row.name.trim() || 'этап'}`,
      }));
    }
    return existingStages.map((row) => ({
      id: String(row.id),
      label: `${row.stage_number}. ${row.name}`,
    }));
  }, [contractMode, stageDrafts, existingStages]);

  const validate = (): string | null => {
    if (contractorMode === 'select') {
      if (selectedContractorId == null && contractMode === 'create') {
        return 'Выберите контрагента';
      }
    } else {
      if (!contractorForm.name.trim()) return 'Укажите название контрагента';
      if (contractorForm.contractor_type === 'ЮЛ' && !contractorForm.inn.trim()) {
        return 'Укажите ИНН для юридического лица';
      }
      if (contractorForm.contractor_type === 'ЮЛ' && !contractorForm.address.trim()) {
        return 'Укажите юридический адрес';
      }
    }

    if (contractMode === 'select') {
      if (selectedContractId == null) return 'Выберите договор';
    } else {
      if (contractorMode === 'select' && selectedContractorId == null) {
        return 'Выберите контрагента';
      }
      if (!contractForm.contract_number.trim()) return 'Укажите номер договора';
      if (hasContractNumberWhitespace) return 'Номер договора не должен содержать пробелы';
      if (hasDuplicateContractNumber) {
        return 'Такой номер договора уже существует у выбранного контрагента';
      }
      if (!contractForm.contract_subject.trim()) return 'Укажите предмет договора';
      if (!contractForm.address.trim()) return 'Укажите адрес';
      if (!contractForm.cadastral_number.trim()) return 'Укажите кадастровый номер';
      const stageError = validateStageDrafts(stageDrafts);
      if (stageError) return stageError;
      if (!contractForm.manager_id) return 'Выберите менеджера';
      if (!contractForm.project_lead_id) return 'Выберите РП';
      if (!contractForm.bank_account_id) return 'Выберите счёт оплаты';
      if (canCreateSum) {
        if (!contractForm.total_amount.trim()) return 'Укажите сумму договора';
      } else {
        return 'Нужны права CONTRACT_SUM для суммы и счёта оплаты';
      }
      if (contractForm.is_supplementary) {
        if (selectedContractorId == null) {
          return 'Для ДС сначала выберите существующего контрагента';
        }
        if (!contractForm.main_contract_id) {
          return 'Выберите основной договор для ДС';
        }
      }
    }

    for (let i = 0; i < filledTasks.length; i += 1) {
      const task = filledTasks[i];
      const n = i + 1;
      if (!task.task_id.trim()) return `Задача ${n}: укажите ID`;
      if (!task.short_name.trim()) return `Задача ${n}: укажите название`;
      if (!task.work_type_id) return `Задача ${n}: выберите вид работ`;
      if (stageBindOptions.length > 0) {
        if (task.stageBindIds.length === 0) {
          return `Задача ${n}: привяжите хотя бы один этап работ`;
        }
      } else {
        if (!task.start_date) return `Задача ${n}: укажите дату начала`;
        if (!task.end_date) return `Задача ${n}: укажите крайний срок`;
      }
    }

    const taskIds = filledTasks.map((t) => t.task_id.trim().toLowerCase());
    if (new Set(taskIds).size !== taskIds.length) {
      return 'ID задач в форме должны быть уникальны';
    }

    return null;
  };

  const resetForm = (): void => {
    setContractorMode('select');
    setSelectedContractorId(null);
    setContractorForm(emptyContractorForm());
    setContractMode('select');
    setSelectedContractId(null);
    setContractForm(emptyContractForm());
    setStageDrafts([emptyStageDraft()]);
    setExistingStages([]);
    setTasks([]);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canOpen || submitting) return;

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      setSuccess('');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      let contractorId = selectedContractorId;

      if (contractorMode === 'create') {
        const payload = {
          name: contractorForm.name.trim(),
          contractor_type: contractorForm.contractor_type,
          inn: contractorForm.inn.trim() || undefined,
          address: contractorForm.address.trim() || undefined,
          passport_data: contractorForm.passport_data.trim() || undefined,
          contact_full_name: contractorForm.contact_full_name.trim() || undefined,
          position_relation: contractorForm.position_relation.trim() || undefined,
          phone: contractorForm.phone.trim() || undefined,
          email: contractorForm.email.trim() || undefined,
          additional_contacts: contractorForm.additional_contacts.trim() || undefined,
        };
        const createdContractor = await createContractor(payload);
        contractorId = createdContractor.id;
        setContractors((prev) => [...prev, createdContractor]);
      }

      if (contractorId == null) {
        throw new Error('Не удалось определить контрагента');
      }

      let contractId = selectedContractId;

      if (contractMode === 'create') {
        const dates = contractDatesFromStages(stageDrafts);
        const payload = {
          contractor_id: contractorId,
          contract_number: contractForm.contract_number.trim(),
          contract_subject: contractForm.contract_subject.trim(),
          contract_status: contractForm.contract_status,
          start_date: dates.start_date,
          end_date: dates.end_date,
          total_amount: canCreateSum ? (draftToNumber(contractForm.total_amount) ?? 0) : 0,
          manager_id: Number(contractForm.manager_id),
          project_lead_id: Number(contractForm.project_lead_id),
          address: contractForm.address.trim(),
          cadastral_number: contractForm.cadastral_number.trim(),
          source_id: contractForm.source_id,
          bank_account_id: Number(contractForm.bank_account_id),
          hierarchy_level: contractForm.is_supplementary ? 2 : 1,
          main_contract_id: contractForm.is_supplementary
            ? Number(contractForm.main_contract_id)
            : undefined,
          has_original: contractForm.has_original,
          stages: stagesToWrite(stageDrafts),
        };
        const createdContract = await createContract(payload as Parameters<typeof createContract>[0]);
        contractId = createdContract.id;
        setContracts((prev) => [...prev, createdContract]);
      }

      if (contractId == null) {
        throw new Error('Не удалось определить договор');
      }

      const createdStageRows =
        contractMode === 'create'
          ? await getContractStages(contractId)
          : existingStages;
      const stageIdByBindKey = new Map<string, number>();
      if (contractMode === 'create') {
        stageDrafts.forEach((draft, index) => {
          const row = createdStageRows[index];
          if (row) stageIdByBindKey.set(draft.key, row.id);
        });
      } else {
        createdStageRows.forEach((row) => {
          stageIdByBindKey.set(String(row.id), row.id);
        });
      }

      const createdTasks = [];
      for (const task of filledTasks) {
        const amount = canCreateSum ? (draftToNumber(task.amount) ?? 0) : 0;
        const boundIds = task.stageBindIds
          .map((bindId) => stageIdByBindKey.get(bindId))
          .filter((id): id is number => id != null);
        const boundRows = createdStageRows.filter((row) => boundIds.includes(row.id));
        const boundDates = datesFromBoundStages(boundRows);
        const created = await createTask({
          contract_id: contractId,
          task_id: task.task_id.trim(),
          short_name: task.short_name.trim(),
          work_type_id: Number(task.work_type_id),
          start_date: boundDates.start_date || task.start_date || undefined,
          end_date: boundDates.end_date || task.end_date || undefined,
          amount,
          planned_cost: canCreateCost ? draftToNumber(task.planned_cost) : undefined,
          taxes: canCreateCost ? draftToNumber(task.taxes) : undefined,
          service_fee: canCreateCost ? draftToNumber(task.service_fee) : undefined,
          partner_commission: canCreateMargin
            ? draftToNumber(task.partner_commission)
            : undefined,
          planned_margin: canCreateMargin
            ? draftToNumber(task.planned_margin)
            : undefined,
          adjustment: canCreateMargin ? draftToNumber(task.adjustment) : undefined,
          manager_id: task.manager_id ? Number(task.manager_id) : undefined,
          project_lead_id: task.project_lead_id ? Number(task.project_lead_id) : undefined,
          department_ids: task.executors.department_ids,
          assignee_employee_ids: task.executors.assignee_employee_ids,
          contract_stage_ids: boundIds,
          task_status: task.task_status,
        } as Parameters<typeof createTask>[0]);
        createdTasks.push(created);
      }

      setSuccess(
        createdTasks.length > 0
          ? `Готово: контрагент, договор и ${createdTasks.length} задач(и). Переход к договору…`
          : 'Готово: контрагент и договор. Переход к договору…',
      );
      resetForm();
      navigate(`/contracts/${contractId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при сохранении');
    } finally {
      setSubmitting(false);
    }
  };

  if (!canOpen) {
    return (
      <div className="data-page into-work-page">
        <div className="empty-state">
          Нет доступа. Нужны права CONTRACTORS/CREATE, CONTRACTS/CREATE, TASKS/CREATE и отдел ALL_COMP.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="data-page into-work-page">
        <div className="loading">Загрузка…</div>
      </div>
    );
  }

  return (
    <div className="data-page into-work-page">
      <div className="page-header-row">
        <h1>Создать контрагента /договор / задачу</h1>
      </div>
      <p className="into-work__lead">
        Создание цепочки: контрагент → договор → задачи. Можно выбрать существующие записи или создать новые.
      </p>

      {error ? <div className="error-message">{error}</div> : null}
      {success ? <div className="into-work__success">{success}</div> : null}

      <form className="form into-work__form" onSubmit={handleSubmit}>
        <section className="into-work__section">
          <div className="into-work__section-head">
            <h2>1. Контрагент</h2>
            <ModeToggle
              value={contractorMode}
              onChange={handleContractorMode}
              selectLabel="Выбрать"
              createLabel="Создать"
            />
          </div>

          {contractorMode === 'select' ? (
            <div className="form-row">
              <div className="form-group into-work__grow">
                <label>
                  Контрагент <span className="into-work__req">*</span>
                </label>
                <ContractorPicker
                  contractors={contractors}
                  value={selectedContractorId}
                  onChange={handleSelectContractor}
                  emptyLabel="Выберите контрагента"
                  placeholder="Название или ИНН"
                  ariaLabel="Контрагент"
                />
              </div>
            </div>
          ) : (
            <>
              <div className="form-row">
                <div className="form-group into-work__grow">
                  <label>
                    Название <span className="into-work__req">*</span>
                  </label>
                  <input
                    type="text"
                    value={contractorForm.name}
                    onChange={(e) =>
                      setContractorForm({ ...contractorForm, name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>
                    Тип <span className="into-work__req">*</span>
                  </label>
                  <SearchableSelect
                    options={[
                      { value: 'ЮЛ', label: 'ЮЛ' },
                      { value: 'ФЛ', label: 'ФЛ' },
                    ]}
                    value={contractorForm.contractor_type}
                    onChange={(value) =>
                      setContractorForm({
                        ...contractorForm,
                        contractor_type: value as 'ФЛ' | 'ЮЛ',
                      })
                    }
                    allowEmpty={false}
                    emptyLabel="Тип"
                    ariaLabel="Тип контрагента"
                  />
                </div>
                <div className="form-group">
                  <label>
                    ИНН
                    {contractorForm.contractor_type === 'ЮЛ' ? (
                      <span className="into-work__req"> *</span>
                    ) : null}
                  </label>
                  <input
                    type="text"
                    value={contractorForm.inn}
                    onChange={(e) =>
                      setContractorForm({ ...contractorForm, inn: e.target.value })
                    }
                    maxLength={12}
                    required={contractorForm.contractor_type === 'ЮЛ'}
                  />
                </div>
              </div>

              <div className="form-row">
                {contractorForm.contractor_type === 'ЮЛ' ? (
                  <div className="form-group into-work__grow">
                    <label>
                      Юридический адрес <span className="into-work__req">*</span>
                    </label>
                    <input
                      type="text"
                      value={contractorForm.address}
                      onChange={(e) =>
                        setContractorForm({ ...contractorForm, address: e.target.value })
                      }
                      required
                    />
                  </div>
                ) : (
                  <div className="form-group into-work__grow">
                    <label>Паспортные данные</label>
                    <input
                      type="text"
                      value={contractorForm.passport_data}
                      onChange={(e) =>
                        setContractorForm({
                          ...contractorForm,
                          passport_data: e.target.value,
                        })
                      }
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>ФИО для связи</label>
                  <input
                    type="text"
                    value={contractorForm.contact_full_name}
                    onChange={(e) =>
                      setContractorForm({
                        ...contractorForm,
                        contact_full_name: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Должность / отношение</label>
                  <input
                    type="text"
                    value={contractorForm.position_relation}
                    onChange={(e) =>
                      setContractorForm({
                        ...contractorForm,
                        position_relation: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Телефон</label>
                  <input
                    type="tel"
                    value={contractorForm.phone}
                    onChange={(e) =>
                      setContractorForm({ ...contractorForm, phone: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={contractorForm.email}
                    onChange={(e) =>
                      setContractorForm({ ...contractorForm, email: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group into-work__grow">
                  <label>Дополнительные контакты</label>
                  <input
                    type="text"
                    value={contractorForm.additional_contacts}
                    onChange={(e) =>
                      setContractorForm({
                        ...contractorForm,
                        additional_contacts: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </>
          )}
        </section>

        <section className="into-work__section">
          <div className="into-work__section-head">
            <h2>2. Договор</h2>
            <ModeToggle
              value={contractMode}
              onChange={handleContractMode}
              selectLabel="Выбрать"
              createLabel="Создать"
              disabledSelect={contractorMode === 'create'}
            />
          </div>

          {contractMode === 'select' ? (
            <div className="form-row">
              <div className="form-group into-work__grow">
                <label>
                  Договор <span className="into-work__req">*</span>
                </label>
                <ContractPicker
                  contracts={contractPickerContracts}
                  contractorsById={contractorsById}
                  value={selectedContractId}
                  onChange={handleSelectContract}
                  disabled={contractorMode === 'create'}
                  emptyLabel={
                    contractorMode === 'create'
                      ? 'Сначала создайте контрагента'
                      : 'Выберите договор'
                  }
                />
              </div>
            </div>
          ) : (
            <>
              {contractorMode === 'create' && !effectiveContractorId ? (
                <p className="into-work__hint">
                  Договор будет создан для нового контрагента из блока выше.
                </p>
              ) : null}

              <div className="form-row">
                <div className="form-group">
                  <label>
                    Номер <span className="into-work__req">*</span>
                  </label>
                  <input
                    type="text"
                    value={contractForm.contract_number}
                    onChange={(e) =>
                      setContractForm({ ...contractForm, contract_number: e.target.value })
                    }
                    aria-invalid={hasContractNumberWhitespace || hasDuplicateContractNumber}
                    required
                  />
                </div>
                <div className="form-group into-work__field-with-check">
                  <label>ДС</label>
                  <div className="into-work__control-with-check">
                    <label className="into-work__inline-check">
                      <input
                        type="checkbox"
                        checked={contractForm.is_supplementary}
                        onChange={(e) =>
                          setContractForm({
                            ...contractForm,
                            is_supplementary: e.target.checked,
                            main_contract_id: '',
                          })
                        }
                      />
                      <span>Доп. соглашение</span>
                    </label>
                  </div>
                </div>
                {contractForm.is_supplementary ? (
                  <div className="form-group">
                    <label>
                      Основной договор <span className="into-work__req">*</span>
                    </label>
                    <SearchableSelect
                      options={mainContractOptions}
                      value={contractForm.main_contract_id}
                      onChange={(value) =>
                        setContractForm({ ...contractForm, main_contract_id: value })
                      }
                      emptyLabel={
                        selectedContractorId == null
                          ? 'Сначала выберите контрагента'
                          : 'Выберите'
                      }
                      placeholder="Поиск номера"
                      ariaLabel="Основной договор"
                      disabled={selectedContractorId == null}
                    />
                  </div>
                ) : null}
              </div>

              <div className="form-row">
                <div className="form-group into-work__grow">
                  <label>
                    Предмет договора <span className="into-work__req">*</span>
                  </label>
                  <input
                    type="text"
                    value={contractForm.contract_subject}
                    onChange={(e) =>
                      setContractForm({ ...contractForm, contract_subject: e.target.value })
                    }
                    maxLength={200}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group into-work__grow">
                  <label>
                    Адрес <span className="into-work__req">*</span>
                  </label>
                  <input
                    type="text"
                    value={contractForm.address}
                    onChange={(e) =>
                      setContractForm({ ...contractForm, address: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group into-work__grow">
                  <label>
                    Кадастровый номер <span className="into-work__req">*</span>
                  </label>
                  <input
                    type="text"
                    value={contractForm.cadastral_number}
                    onChange={(e) =>
                      setContractForm({
                        ...contractForm,
                        cadastral_number: e.target.value,
                      })
                    }
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group into-work__grow">
                  <label>
                    График работ <span className="into-work__req">*</span>
                  </label>
                  <ContractStagesEditor
                    drafts={stageDrafts}
                    onChange={setStageDrafts}
                    canEdit
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    Сумма <span className="into-work__req">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="into-work__money-input"
                    value={displayContractSumDraft(contractForm.total_amount)}
                    onChange={(e) =>
                      setContractForm({
                        ...contractForm,
                        total_amount: normalizeContractSumDraft(e.target.value),
                      })
                    }
                    required
                    disabled={!canCreateSum}
                  />
                </div>
                <div className="form-group into-work__field-with-check">
                  <label>
                    Счёт оплаты <span className="into-work__req">*</span>
                  </label>
                  <div className="into-work__control-with-check">
                    <div className="into-work__control-with-check-field">
                      <SearchableSelect
                        options={bankAccountOptions}
                        value={contractForm.bank_account_id}
                        onChange={(value) =>
                          setContractForm({ ...contractForm, bank_account_id: value })
                        }
                        emptyLabel="Выберите"
                        placeholder="Поиск счёта"
                        ariaLabel="Счёт оплаты"
                      />
                    </div>
                    <label className="into-work__inline-check">
                      <input
                        type="checkbox"
                        checked={contractForm.has_original}
                        onChange={(e) =>
                          setContractForm({
                            ...contractForm,
                            has_original: e.target.checked,
                          })
                        }
                      />
                      <span>Подписан</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    Менеджер <span className="into-work__req">*</span>
                  </label>
                  <SearchableSelect
                    options={managerOptions}
                    value={contractForm.manager_id}
                    onChange={(value) =>
                      setContractForm({ ...contractForm, manager_id: value })
                    }
                    emptyLabel="Выберите"
                    placeholder="Поиск"
                    ariaLabel="Менеджер"
                  />
                </div>
                <div className="form-group">
                  <label>
                    РП <span className="into-work__req">*</span>
                  </label>
                  <SearchableSelect
                    options={projectLeadOptions}
                    value={contractForm.project_lead_id}
                    onChange={(value) =>
                      setContractForm({ ...contractForm, project_lead_id: value })
                    }
                    emptyLabel="Выберите"
                    placeholder="Поиск"
                    ariaLabel="РП"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Источник</label>
                  <SourcePicker
                    sources={sourcesLevel2}
                    value={contractForm.source_id}
                    onChange={(id) =>
                      setContractForm({ ...contractForm, source_id: id })
                    }
                    allowCreate
                    onCreate={async (name) => {
                      const created = await createSource({ name, level: 2 });
                      setSourcesLevel2((prev) =>
                        prev.some((s) => s.id === created.id)
                          ? prev
                          : [...prev, created].sort((a, b) =>
                              a.name.localeCompare(b.name, 'ru'),
                            ),
                      );
                      setContractForm((prev) => ({ ...prev, source_id: created.id }));
                    }}
                    emptyLabel="—"
                    placeholder="Поиск или введите новый"
                    ariaLabel="Источник"
                  />
                </div>
              </div>

              {(hasContractNumberWhitespace || hasDuplicateContractNumber) && (
                <div className="into-work__inline-error">
                  {hasContractNumberWhitespace
                    ? 'Номер договора не должен содержать пробелы.'
                    : 'Такой номер договора уже существует у выбранного контрагента.'}
                </div>
              )}
            </>
          )}
        </section>

        <section className="into-work__section">
          <div className="into-work__section-head">
            <h2>3. Задачи</h2>
            <button type="button" className="secondary-button" onClick={addTask}>
              + Задача
            </button>
          </div>

          {tasks.length === 0 ? (
            <p className="into-work__hint">Задачи необязательны. Нажмите «+ Задача», чтобы добавить.</p>
          ) : null}

          {tasks.map((task, index) => {
            const columnTask: Task = {
              id: index + 1,
              contracts_task_id: index + 1,
              contract_id: selectedContractId ?? 0,
              task_id: task.task_id.trim() || `новая-${index + 1}`,
              short_name: task.short_name,
              work_type_id: Number(task.work_type_id) || 0,
              start_date: task.start_date,
              amount: draftToNumber(task.amount) ?? 0,
              planned_cost: draftToNumber(task.planned_cost),
              taxes: draftToNumber(task.taxes),
              service_fee: draftToNumber(task.service_fee),
              partner_commission: draftToNumber(task.partner_commission),
              planned_margin: draftToNumber(task.planned_margin),
              adjustment: draftToNumber(task.adjustment),
              contract_number:
                contractMode === 'create'
                  ? contractForm.contract_number.trim() || '—'
                  : contracts.find((c) => c.id === selectedContractId)?.contract_number ?? '—',
            };
            const columnTasks = [columnTask];
            const contractsByIdForHeader = new Map<number, Contract>();
            if (selectedContractId != null) {
              const selected = contracts.find((c) => c.id === selectedContractId);
              if (selected) contractsByIdForHeader.set(selected.id, selected);
            } else if (contractMode === 'create') {
              contractsByIdForHeader.set(0, {
                id: 0,
                contractor_id: selectedContractorId ?? 0,
                contract_number: contractForm.contract_number.trim() || '—',
                contract_status: contractForm.contract_status,
                start_date: contractForm.start_date,
                total_amount: draftToNumber(contractForm.total_amount) ?? 0,
              });
            }
            const headerCells = buildMarginCalcHeaderCells(
              columnTasks,
              contractsByIdForHeader,
              (id) => workTypes.find((wt) => wt.id === id)?.name ?? '—',
            );
            const otherBudgetKeys = marginFinanceRows
              .filter((row) => row.key !== 'amount' && !row.computed && row.draftKey)
              .map((row) => row.key);
            const tableData = buildMarginCalcTableData({
              financeRows: marginFinanceRows,
              columnTasks,
              valueForColumn: (rowKey, column) => {
                if (rowKey === BUDGET_EXTRA_EXPENSES_KEY) {
                  const amount = taskMarginFieldValue('amount', column);
                  if (amount == null) return null;
                  const othersSum = otherBudgetKeys.reduce(
                    (sum, key) => sum + (taskMarginFieldValue(key, column) ?? 0),
                    0,
                  );
                  return amount - othersSum;
                }
                return taskMarginFieldValue(rowKey, column);
              },
            });

            return (
              <div key={task.key} className="into-work__task">
                <div className="into-work__task-head">
                  <h3>Задача {index + 1}</h3>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => removeTask(task.key)}
                  >
                    Удалить
                  </button>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      ID <span className="into-work__req">*</span>
                    </label>
                    <input
                      type="text"
                      value={task.task_id}
                      onChange={(e) => updateTask(task.key, { task_id: e.target.value })}
                      maxLength={50}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      Вид работ <span className="into-work__req">*</span>
                    </label>
                    <SearchableSelect
                      options={workTypeOptions}
                      value={task.work_type_id}
                      onChange={(value) => updateTask(task.key, { work_type_id: value })}
                      emptyLabel="Выберите"
                      placeholder="Поиск"
                      ariaLabel={`Вид работ задачи ${index + 1}`}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group into-work__grow">
                    <label>
                      Этапы работ {stageBindOptions.length > 0 ? <span className="into-work__req">*</span> : null}
                    </label>
                    {stageBindOptions.length > 0 ? (
                      <StageBindPicker
                        options={stageBindOptions}
                        selectedIds={task.stageBindIds}
                        onChange={(ids) => updateTask(task.key, { stageBindIds: ids })}
                        canEdit
                        disabled={submitting}
                      />
                    ) : (
                      <div className="form-row">
                        <div className="form-group">
                          <label>
                            Начало <span className="into-work__req">*</span>
                          </label>
                          <input
                            type="date"
                            value={task.start_date}
                            onChange={(e) => updateTask(task.key, { start_date: e.target.value })}
                            onPaste={(e) =>
                              handleDatePaste(e, (value) => updateTask(task.key, { start_date: value }))
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label>
                            Крайний срок <span className="into-work__req">*</span>
                          </label>
                          <input
                            type="date"
                            value={task.end_date}
                            onChange={(e) => updateTask(task.key, { end_date: e.target.value })}
                            onPaste={(e) =>
                              handleDatePaste(e, (value) => updateTask(task.key, { end_date: value }))
                            }
                            required
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group into-work__grow">
                    <label>
                      Название <span className="into-work__req">*</span>
                    </label>
                    <input
                      type="text"
                      value={task.short_name}
                      readOnly
                      maxLength={200}
                      title="Формируется автоматически: [Вид работ]: [Контрагент] / [Адрес]"
                    />
                  </div>
                </div>

                {marginFinanceRows.length > 0 ? (
                  <div className="into-work__margin-wrap">
                    <MarginCalcTable
                      columnTasks={columnTasks}
                      headerCells={headerCells}
                      tableData={tableData}
                      editable
                      getDraftValue={(_contractsTaskId, financeKey) => {
                        const key = financeKey as TaskFinanceKey;
                        return task[key] ?? '';
                      }}
                      onDraftChange={(_contractsTaskId, financeKey, value) => {
                        const key = financeKey as TaskFinanceKey;
                        updateTask(task.key, {
                          [key]: normalizeContractSumDraft(value),
                        } as Partial<TaskForm>);
                      }}
                    />
                  </div>
                ) : null}

                <div className="form-row">
                  <div className="form-group">
                    <label>Менеджер (если не из договора)</label>
                    <SearchableSelect
                      options={managerOptions}
                      value={task.manager_id}
                      onChange={(value) => updateTask(task.key, { manager_id: value })}
                      emptyLabel="Из договора"
                      placeholder="Поиск"
                      ariaLabel={`Менеджер задачи ${index + 1}`}
                    />
                  </div>
                  <div className="form-group">
                    <label>РП (если не из договора)</label>
                    <SearchableSelect
                      options={projectLeadOptions}
                      value={task.project_lead_id}
                      onChange={(value) => updateTask(task.key, { project_lead_id: value })}
                      emptyLabel="Из договора"
                      placeholder="Поиск"
                      ariaLabel={`РП задачи ${index + 1}`}
                    />
                  </div>
                </div>

                <div className="into-work__executors">
                  <div className="into-work__executors-label">Исполнители</div>
                  <TaskExecutorsPanel
                    departments={executorDepartments}
                    employeesByDepartment={employeesByDepartment}
                    value={task.executors}
                    onChange={(next) => updateTask(task.key, { executors: next })}
                    disabled={submitting}
                  />
                </div>
              </div>
            );
          })}
        </section>

        <div className="form-actions into-work__actions">
          <button type="submit" className="submit-button" disabled={submitting}>
            {submitting ? 'Сохранение…' : 'Ввести в работу'}
          </button>
          <button
            type="button"
            className="secondary-button"
            onClick={resetForm}
            disabled={submitting}
          >
            Сбросить
          </button>
        </div>
      </form>
    </div>
  );
};

export default IntoWorkPage;
